import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
// config
import { AppName, AppUrl } from '../../config';

// ----------------------------------------------------------------------

// Default 1200x630-ish social card. Kept as a root-relative path so callers can
// override it; the component resolves it against AppUrl.
const DEFAULT_SHARE_IMAGE = '/assets/logos/asap.png';

// Canonical paths must be host-free, leading-slash, and trailing-slash free so
// a page and its detail route never produce two different canonicals.
function toCanonicalPath(path) {
  if (!path || path === '/') {
    return '/';
  }

  const withLeading = path.startsWith('/') ? path : `/${path}`;

  return withLeading.endsWith('/') ? withLeading.slice(0, -1) : withLeading;
}

export function toAbsoluteUrl(value) {
  if (!value) {
    return `${AppUrl}${DEFAULT_SHARE_IMAGE}`;
  }

  return value.startsWith('http') ? value : `${AppUrl}${toCanonicalPath(value)}`;
}

// Meta descriptions must stay short. Detail pages build theirs from API copy
// (service intro, course introduction), which can run long, so trim on a word
// boundary and add an ellipsis.
export function truncateText(value, max = 160) {
  const text = String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= max) {
    return text;
  }

  return `${text.slice(0, text.lastIndexOf(' ', max - 1)).trim()}…`;
}

// A caller may pass "Courses" or already pass a fully branded title; never
// append the brand twice.
function withBrand(title) {
  if (!title) {
    return AppName;
  }

  return title.includes(AppName) ? title : `${title} | ${AppName}`;
}

// ----------------------------------------------------------------------

/**
 * Shared document head for every public page.
 *
 * @param {string} title            Page title without the brand suffix.
 * @param {string} description      Unique 140-160 character description.
 * @param {string} path             Canonical path, e.g. `/courses`.
 * @param {string} image            Root-relative or absolute share image.
 * @param {string} type             Open Graph type (`website`, `article`, ...).
 * @param {string} robots           Robots directive for this page.
 * @param {object|array} jsonLd     One or more schema.org blocks.
 * @param {node} children           Extra head tags (e.g. article metadata).
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_SHARE_IMAGE,
  type = 'website',
  robots = 'index, follow',
  jsonLd,
  children,
}) {
  const pageTitle = withBrand(title);
  const canonical = `${AppUrl}${toCanonicalPath(path)}`;
  const imageUrl = toAbsoluteUrl(image);
  const blocks = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  return (
    <Helmet>
      <title>{pageTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={AppName} />
      <meta property="og:title" content={pageTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={pageTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />

      {blocks.map((block, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.string,
  robots: PropTypes.string,
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  children: PropTypes.node,
};
